import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Send, CheckCircle, AlertCircle, Info, Upload, X, ZoomIn, ZoomOut, Move,
  Gavel, Shield, Wrench, Car, Building2, Truck, Star, Award, Sparkles,
  ArrowLeft, Image, ChevronDown, DollarSign, Megaphone, Eye, Monitor, Smartphone,
  TrendingUp, Target, Clock, Percent, ChevronRight, FileText
} from 'lucide-react';
import { db, storage, listingTiers } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { TopBanner, BottomBanner } from './BannerAd';

// ==================== PARISHES ====================
const PARISHES = [
  'Kingston', 'St. Andrew', 'St. Thomas', 'Portland', 'St. Mary',
  'St. Ann', 'Trelawny', 'St. James', 'Hanover', 'Westmoreland',
  'St. Elizabeth', 'Manchester', 'Clarendon', 'St. Catherine'
];

// ==================== BUSINESS TYPES (with wrecker) ====================
const BUSINESS_TYPES = [
  { id: 'lawyer', label: 'Traffic Lawyer', icon: Gavel, description: 'Legal representation for traffic offences' },
  { id: 'insurance', label: 'Insurance Company', icon: Shield, description: 'Motor vehicle insurance provider' },
  { id: 'evaluator', label: 'Vehicle Inspector', icon: Wrench, description: 'Fitness testing and evaluation' },
  { id: 'mechanic', label: 'Mechanic / Garage', icon: Wrench, description: 'Auto repair and maintenance' },
  { id: 'wrecker', label: 'Wrecker Service', icon: Truck, description: 'Towing and roadside assistance' },
  { id: 'parts', label: 'Auto Parts Store', icon: Car, description: 'Vehicle parts and accessories' },
  { id: 'dealership', label: 'Car Dealership', icon: Car, description: 'New and used vehicle sales' },
  { id: 'other', label: 'Other', icon: Building2, description: 'Other motoring-related service' }
];

// Default listing tiers - shown immediately and used as fallback
const DEFAULT_TIERS = [
  {
    name: 'free',
    display_name: 'Free',
    price_monthly: 0,
    features: { basicContact: true, logo: false, website: false, description: false, services: false, analytics: false, priority: false, verified: false },
    description: 'Get discovered with basic contact info'
  },
  {
    name: 'basic',
    display_name: 'Basic',
    price_monthly: 3500,
    features: { basicContact: true, logo: true, website: true, description: true, services: true, analytics: true, priority: false, verified: false },
    description: 'Full business profile with logo and analytics'
  },
  {
    name: 'premium',
    display_name: 'Premium',
    price_monthly: 5000,
    features: { basicContact: true, logo: true, website: true, description: true, services: true, analytics: true, priority: true, verified: true, highlighted: true },
    description: 'Maximum visibility with priority placement'
  }
];

// Default banner ad packages - fallback if database not available
const DEFAULT_BANNER_PACKAGES = [
  // Top Banner
  { name: 'top_3m', display_name: 'Top Banner', placement: 'top', duration_months: 3, price: 15000, discount_percent: 0, description: 'Prime visibility at the top of every page' },
  { name: 'top_6m', display_name: 'Top Banner', placement: 'top', duration_months: 6, price: 27000, discount_percent: 10, description: 'Prime visibility at the top of every page' },
  { name: 'top_9m', display_name: 'Top Banner', placement: 'top', duration_months: 9, price: 36000, discount_percent: 20, description: 'Prime visibility at the top of every page' },
  { name: 'top_12m', display_name: 'Top Banner', placement: 'top', duration_months: 12, price: 42000, discount_percent: 20, description: 'Prime visibility at the top of every page' },
  // Bottom Banner
  { name: 'bottom_3m', display_name: 'Bottom Banner', placement: 'bottom', duration_months: 3, price: 10000, discount_percent: 0, description: 'Visible footer position on all pages' },
  { name: 'bottom_6m', display_name: 'Bottom Banner', placement: 'bottom', duration_months: 6, price: 18000, discount_percent: 10, description: 'Visible footer position on all pages' },
  { name: 'bottom_9m', display_name: 'Bottom Banner', placement: 'bottom', duration_months: 9, price: 24000, discount_percent: 20, description: 'Visible footer position on all pages' },
  { name: 'bottom_12m', display_name: 'Bottom Banner', placement: 'bottom', duration_months: 12, price: 28000, discount_percent: 20, description: 'Visible footer position on all pages' },
  // Inline Banner
  { name: 'inline_3m', display_name: 'In-Content Banner', placement: 'inline', duration_months: 3, price: 8000, discount_percent: 0, description: 'Placed within page content for high engagement' },
  { name: 'inline_6m', display_name: 'In-Content Banner', placement: 'inline', duration_months: 6, price: 14400, discount_percent: 10, description: 'Placed within page content for high engagement' },
  { name: 'inline_9m', display_name: 'In-Content Banner', placement: 'inline', duration_months: 9, price: 19200, discount_percent: 20, description: 'Placed within page content for high engagement' },
  { name: 'inline_12m', display_name: 'In-Content Banner', placement: 'inline', duration_months: 12, price: 22400, discount_percent: 20, description: 'Placed within page content for high engagement' }
];

// Duration options - max discount capped at 20%
const DURATION_OPTIONS = [
  { months: 3, label: '3 Months', discount: 0 },
  { months: 6, label: '6 Months', discount: 10 },
  { months: 9, label: '9 Months', discount: 15 },
  { months: 12, label: '12 Months', discount: 20, popular: true }
];

// ==================== IMAGE CROPPER COMPONENT ====================
const ImageCropper = ({ image, onCrop, onCancel }) => {
  const [position, setPosition] = useState({ x: 50, y: 50 });
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const handleMouseDown = (e) => {
    setIsDragging(true);
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setPosition({
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y))
    });
  }, [isDragging]);

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove]);

  const handleSave = () => {
    onCrop({ x: position.x, y: position.y, zoom });
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="p-4 border-b">
          <h3 className="font-bold text-lg">Position Your Logo</h3>
          <p className="text-sm text-gray-500">Drag to reposition, use zoom to adjust size</p>
        </div>

        {/* Preview Area */}
        <div
          ref={containerRef}
          className="relative w-full aspect-square bg-gray-100 overflow-hidden cursor-move"
          onMouseDown={handleMouseDown}
        >
          {/* Crop Guide */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-32 h-32 border-4 border-white/50 rounded-full shadow-lg" />
          </div>

          {/* Image */}
          <img
            src={image}
            alt="Preview"
            className="absolute w-full h-full object-cover transition-transform"
            style={{
              transform: `scale(${zoom})`,
              objectPosition: `${position.x}% ${position.y}%`
            }}
            draggable={false}
          />
        </div>

        {/* Controls */}
        <div className="p-4 space-y-4">
          {/* Zoom Slider */}
          <div className="flex items-center gap-4">
            <ZoomOut className="w-5 h-5 text-gray-400" />
            <input
              type="range"
              min="1"
              max="3"
              step="0.1"
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="flex-1"
            />
            <ZoomIn className="w-5 h-5 text-gray-400" />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl"
            >
              Apply
            </button>
            <button
              onClick={onCancel}
              className="flex-1 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold rounded-xl"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== ENHANCED GET LISTED PAGE ====================
export const GetListedPageEnhanced = ({ setPage, goBack, lang }) => {
  const { user, isAuthenticated, profile, updateProfile } = useAuth();

  // Check if user is a motorist (will need account conversion)
  const isMotoristAccount = isAuthenticated && profile?.user_type !== 'service_provider';
  const [form, setForm] = useState({
    tier: 'premium', // Pre-select Premium tier
    billingPeriod: 'monthly', // monthly or annual
    businessType: '',
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    parish: 'Kingston',
    address: '',
    website: '',
    services: '',
    description: '',
    // Business hours - structured as object with days
    businessHours: {
      monday: { open: '09:00', close: '17:00', closed: false },
      tuesday: { open: '09:00', close: '17:00', closed: false },
      wednesday: { open: '09:00', close: '17:00', closed: false },
      thursday: { open: '09:00', close: '17:00', closed: false },
      friday: { open: '09:00', close: '17:00', closed: false },
      saturday: { open: '09:00', close: '13:00', closed: false },
      sunday: { open: '', close: '', closed: true }
    },
    // Banner ad interest fields
    interestedInBannerAd: false,
    bannerAdPlacement: '',
    bannerAdDuration: 0,
    bannerAdNotes: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoPosition, setLogoPosition] = useState({ x: 50, y: 50, zoom: 1 });
  const [showCropper, setShowCropper] = useState(false);
  // Initialize with default tiers so they show immediately for all users
  const [tiers, setTiers] = useState(DEFAULT_TIERS);
  const [bannerPackages, setBannerPackages] = useState(DEFAULT_BANNER_PACKAGES);
  const [submitted, setSubmitted] = useState(false);
  const [submittedTier, setSubmittedTier] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showBannerDetails, setShowBannerDetails] = useState(false);
  // Payment proof for paid tiers
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [paymentProofPreview, setPaymentProofPreview] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState({
    method: '',
    transactionRef: '',
    paymentDate: ''
  });
  const fileInputRef = useRef(null);
  const paymentProofRef = useRef(null);

  // Auto-fill email from authenticated user
  useEffect(() => {
    if (user?.email && !form.email) {
      setForm(prev => ({
        ...prev,
        email: user.email
      }));
    }
  }, [user]);

  useEffect(() => {
    loadTiers();
    loadBannerPackages();
  }, []);

  const loadTiers = async () => {
    const { data, error } = await listingTiers.getAll();
    // Use default tiers if fetch fails or returns empty
    if (error || !data || data.length === 0) {
      setTiers(DEFAULT_TIERS);
    } else {
      setTiers(data);
    }
  };

  const loadBannerPackages = async () => {
    const { data, error } = await bannerAdPackages.getAll();
    if (error || !data || data.length === 0) {
      setBannerPackages(DEFAULT_BANNER_PACKAGES);
    } else {
      setBannerPackages(data);
    }
  };

  // Get selected banner package price
  const getSelectedBannerPrice = () => {
    if (!form.bannerAdPlacement || !form.bannerAdDuration) return 0;
    const pkg = bannerPackages.find(
      p => p.placement === form.bannerAdPlacement && p.duration_months === form.bannerAdDuration
    );
    return pkg ? pkg.price : 0;
  };

  // Get monthly cost for a placement
  const getMonthlyPrice = (placement) => {
    const pkg = bannerPackages.find(p => p.placement === placement && p.duration_months === 3);
    return pkg ? Math.round(pkg.price / 3) : 0;
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (event) => {
      setLogoPreview(event.target.result);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
    setError('');
  };

  const handleCropComplete = (cropData) => {
    setLogoPosition(cropData);
    setShowCropper(false);
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setLogoPosition({ x: 50, y: 50, zoom: 1 });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Payment proof handlers
  const handlePaymentProofSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type (image or PDF)
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      setError('Please select an image or PDF file for payment proof');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Payment proof must be less than 10MB');
      return;
    }

    setPaymentProofFile(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => setPaymentProofPreview(event.target.result);
      reader.readAsDataURL(file);
    } else {
      setPaymentProofPreview(null); // PDF - no preview
    }
    setError('');
  };

  const removePaymentProof = () => {
    setPaymentProofFile(null);
    setPaymentProofPreview(null);
    if (paymentProofRef.current) paymentProofRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!form.tier) {
      setError('Please select a listing tier');
      return;
    }
    if (!form.businessType) {
      setError('Please select a business type');
      return;
    }
    if (!form.businessName || !form.contactName || !form.email || !form.phone) {
      setError('Please fill in all required fields (Business Name, Contact Name, Email, Phone)');
      return;
    }

    // Validate payment proof for paid tiers
    if (form.tier !== 'free' && !paymentProofFile) {
      setError('Please upload proof of payment for paid listings');
      return;
    }

    if (form.tier !== 'free' && !paymentInfo.method) {
      setError('Please select your payment method');
      return;
    }

    setSubmitting(true);

    try {
      // Upload logo if present
      let logoUrl = null;
      if (logoFile && (form.tier !== 'free')) {
        const ext = logoFile.name.split('.').pop();
        const path = `logos/${Date.now()}_${form.businessName.replace(/\s+/g, '_')}.${ext}`;
        const { error: uploadError } = await storage.uploadImage('business-logos', path, logoFile);

        if (!uploadError) {
          logoUrl = storage.getPublicUrl('business-logos', path);
        }
      }

      // Upload payment proof if present (paid tiers)
      let paymentProofUrl = null;
      let paymentProofFilename = null;
      if (paymentProofFile && form.tier !== 'free') {
        const ext = paymentProofFile.name.split('.').pop();
        paymentProofFilename = paymentProofFile.name;
        const path = `payment-proofs/${Date.now()}_${form.businessName.replace(/\s+/g, '_')}_proof.${ext}`;
        const { error: uploadError } = await storage.uploadImage('business-logos', path, paymentProofFile);

        if (!uploadError) {
          paymentProofUrl = storage.getPublicUrl('business-logos', path);
        }
      }

      // Calculate payment amount
      const paymentAmount = form.tier !== 'free' ? (
        form.billingPeriod === 'annual'
          ? Math.round((tiers.find(t => t.name === form.tier)?.price_monthly || 0) * 12 * 0.9)
          : (tiers.find(t => t.name === form.tier)?.price_monthly || 0)
      ) : 0;

      // Submit to database
      // Build additional_info with tier, logo, and banner interest for robustness
      // (in case extended columns don't exist in DB)
      const additionalInfoParts = [];
      if (form.tier && form.tier !== 'free') {
        additionalInfoParts.push(`TIER: ${form.tier.toUpperCase()}`);
      }
      if (logoUrl) {
        additionalInfoParts.push(`LOGO: ${logoUrl}`);
      }
      if (form.interestedInBannerAd && form.tier !== 'free') {
        additionalInfoParts.push(`BANNER AD INTEREST: ${form.bannerAdPlacement} placement, ${form.bannerAdDuration} months${form.bannerAdNotes ? '. Notes: ' + form.bannerAdNotes : ''}`);
      }

      // Build submission data with extended columns
      const submissionData = {
        business_type: form.businessType,
        business_name: form.businessName,
        contact_name: form.contactName,
        contact_email: form.email,
        contact_phone: form.phone,
        parish: form.parish,
        address: form.address,
        website: form.website,
        description: form.description,
        services: form.services ? form.services.split(',').map(s => s.trim()) : [],
        business_hours: form.businessHours,
        additional_info: additionalInfoParts.length > 0 ? additionalInfoParts.join(' | ') : null,
        tier: form.tier,
        billing_period: form.billingPeriod,
        logo_url: logoUrl,
        logo_position: logoPosition,
        user_id: isAuthenticated && user ? user.id : null,
        payment_status: form.tier === 'free' ? 'verified' : 'pending'
      };

      // Try submission with extended columns first
      let { error: dbError } = await db.submissions.create(submissionData);

      // If error is about missing columns, retry with base schema only
      if (dbError && dbError.message && dbError.message.includes('column')) {
        console.warn('Extended columns not available, using base schema:', dbError.message);
        const baseSubmissionData = {
          business_type: form.businessType,
          business_name: form.businessName,
          contact_name: form.contactName,
          contact_email: form.email,
          contact_phone: form.phone,
          parish: form.parish,
          address: form.address,
          website: form.website,
          description: form.description,
          services: form.services ? form.services.split(',').map(s => s.trim()) : [],
          additional_info: additionalInfoParts.length > 0 ? additionalInfoParts.join(' | ') : null
        };
        const retryResult = await db.submissions.create(baseSubmissionData);
        dbError = retryResult.error;
      }

      if (dbError) throw dbError;

      // Convert motorist account to business account automatically
      if (isMotoristAccount) {
        try {
          await updateProfile({ user_type: 'service_provider' });
        } catch (conversionError) {
          console.warn('Account conversion failed:', conversionError);
          // Don't throw - listing was still submitted successfully
        }
      }

      // Store the tier before resetting the form
      setSubmittedTier(form.tier);
      setSubmitted(true);
      setForm({
        tier: '', billingPeriod: 'monthly', businessType: '', businessName: '', contactName: '', email: '', phone: '',
        parish: 'Kingston', address: '', website: '', services: '', description: '',
        interestedInBannerAd: false, bannerAdPlacement: '', bannerAdDuration: 0, bannerAdNotes: '',
        businessHours: {
          monday: { open: '09:00', close: '17:00', closed: false },
          tuesday: { open: '09:00', close: '17:00', closed: false },
          wednesday: { open: '09:00', close: '17:00', closed: false },
          thursday: { open: '09:00', close: '17:00', closed: false },
          friday: { open: '09:00', close: '17:00', closed: false },
          saturday: { open: '09:00', close: '13:00', closed: false },
          sunday: { open: '', close: '', closed: true }
        }
      });
      removeLogo();
      removePaymentProof();
      setPaymentInfo({ method: '', transactionRef: '', paymentDate: '' });
    } catch (err) {
      setError('Error submitting: ' + (err.message || 'Please try again'));
    } finally {
      setSubmitting(false);
    }
  };

  const getTierIcon = (tierName) => {
    switch (tierName) {
      case 'premium': return Award;
      case 'basic': return Star;
      default: return CheckCircle;
    }
  };

  const getTierColor = (tierName) => {
    switch (tierName) {
      case 'premium': return 'amber';
      case 'basic': return 'blue';
      default: return 'gray';
    }
  };

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button onClick={() => goBack ? goBack() : setPage('home')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium">
        <ArrowLeft className="w-5 h-5" />Back
      </button>

      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-violet-700 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3">
          <Send className="w-8 h-8" />Get Listed in Our Directory
        </h2>
        <p className="text-violet-200 mt-2">Reach thousands of Jamaican motorists looking for services like yours</p>
      </div>

      {/* Top Banner Ad */}
      <TopBanner page="getlisted" />

      {/* Image Cropper Modal */}
      {showCropper && logoPreview && (
        <ImageCropper
          image={logoPreview}
          onCrop={handleCropComplete}
          onCancel={() => setShowCropper(false)}
        />
      )}

      {submitted ? (
        <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-8">
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-emerald-900">Submission Received!</h3>
            <p className="text-emerald-700 mt-2">Thank you for your interest. We'll review your submission shortly.</p>
          </div>

          {/* Payment Instructions for Paid Tiers */}
          {submittedTier && submittedTier !== 'free' && (
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex items-start gap-3">
                <DollarSign className="w-6 h-6 text-amber-600 mt-0.5" />
                <div>
                  <h4 className="font-bold text-amber-800">Payment Required to Go Live</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    Your listing is pending payment verification. To activate your {submittedTier === 'premium' ? 'Premium' : 'Basic'} listing:
                  </p>
                  <ol className="text-sm text-amber-700 mt-2 space-y-1 list-decimal list-inside">
                    <li>Make payment to our bank account (details below)</li>
                    <li>Log in to your account and visit your Dashboard</li>
                    <li>Upload your proof of payment (receipt/screenshot)</li>
                    <li>We'll verify and activate your listing within 24-48 hours</li>
                  </ol>
                  <div className="mt-3 p-3 bg-white rounded-lg text-sm">
                    <p className="font-medium text-gray-700">Bank Details:</p>
                    <p className="text-gray-600">Bank: National Commercial Bank (NCB)</p>
                    <p className="text-gray-600">Account Name: JA Motorist Guide Ltd</p>
                    <p className="text-gray-600">Account #: 123-456-789</p>
                    <p className="text-gray-600 font-medium mt-1">Amount: J${submittedTier === 'premium' ? '50,000' : '35,000'}/year</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="text-center mt-6 space-y-3">
            {isAuthenticated ? (
              <button onClick={() => setPage('dashboard')} className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl">
                Go to My Dashboard
              </button>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-gray-600">Create an account to manage your listing and upload payment proof</p>
                <button onClick={() => setPage('login')} className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl">
                  Sign Up / Log In
                </button>
              </div>
            )}
            <button onClick={() => setSubmitted(false)} className="block w-full px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold rounded-xl">
              Submit Another Business
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />{error}
            </div>
          )}

          {/* Listing Tier Selection */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-violet-600" />Choose Your Listing Tier
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              {tiers.map((tier) => {
                const TierIcon = getTierIcon(tier.name);
                const color = getTierColor(tier.name);
                const features = tier.features || {};

                return (
                  <button
                    key={tier.name}
                    type="button"
                    onClick={() => setForm({ ...form, tier: tier.name })}
                    className={`relative p-5 rounded-xl border-2 text-left transition-all h-full flex flex-col ${
                      form.tier === tier.name
                        ? `border-${color}-500 bg-${color}-50 ring-2 ring-${color}-500`
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {tier.name === 'premium' && (
                      <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-amber-500 text-white text-xs font-bold rounded-full">Best Value</span>
                    )}
                    <TierIcon className={`w-8 h-8 text-${color}-600 mb-2`} />
                    <h4 className="font-bold text-lg">{tier.display_name}</h4>
                    <p className="text-2xl font-black text-gray-900 mt-1">
                      {tier.price_monthly > 0 ? `J$${tier.price_monthly.toLocaleString()}` : 'Free'}
                      {tier.price_monthly > 0 && <span className="text-sm font-normal text-gray-500">/mo</span>}
                    </p>
                    <p className="text-sm text-gray-500 mt-2 flex-grow">{tier.description || ''}</p>
                    <ul className="mt-4 space-y-2 text-sm text-gray-600 border-t pt-4">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                        Name, phone, email & parish
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className={`w-4 h-4 ${features.logo ? 'text-emerald-500' : 'text-gray-300'}`} />
                        <span className={features.logo ? '' : 'text-gray-400'}>Business logo</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className={`w-4 h-4 ${features.website ? 'text-emerald-500' : 'text-gray-300'}`} />
                        <span className={features.website ? '' : 'text-gray-400'}>Website & description</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className={`w-4 h-4 ${features.analytics ? 'text-emerald-500' : 'text-gray-300'}`} />
                        <span className={features.analytics ? '' : 'text-gray-400'}>Analytics dashboard</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className={`w-4 h-4 ${features.priority ? 'text-emerald-500' : 'text-gray-300'}`} />
                        <span className={features.priority ? '' : 'text-gray-400'}>Priority placement</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className={`w-4 h-4 ${features.verified ? 'text-emerald-500' : 'text-gray-300'}`} />
                        <span className={features.verified ? '' : 'text-gray-400'}>Verified badge</span>
                      </li>
                    </ul>
                  </button>
                );
              })}
            </div>

            {/* Billing Period Selection - only for paid tiers */}
            {form.tier && form.tier !== 'free' && (
              <div className="mt-6 pt-6 border-t">
                <h4 className="font-bold text-sm text-gray-700 mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" />Billing Period
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, billingPeriod: 'monthly' })}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      form.billingPeriod === 'monthly'
                        ? 'border-violet-500 bg-violet-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="font-bold">Monthly</p>
                    <p className="text-sm text-gray-600">
                      J${(tiers.find(t => t.name === form.tier)?.price_monthly || 0).toLocaleString()}/mo
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, billingPeriod: 'annual' })}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      form.billingPeriod === 'annual'
                        ? 'border-violet-500 bg-violet-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="font-bold">Annual <span className="text-emerald-600 text-xs">(10% off)</span></p>
                    <p className="text-sm text-gray-600">
                      J${Math.round((tiers.find(t => t.name === form.tier)?.price_monthly || 0) * 12 * 0.9).toLocaleString()}/yr
                    </p>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Business Type */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h3 className="font-bold text-lg mb-4">Business Type *</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {BUSINESS_TYPES.map((type) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => setForm({ ...form, businessType: type.id })}
                  className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 transition-all ${
                    form.businessType === type.id
                      ? 'border-violet-500 bg-violet-50 text-violet-700'
                      : 'border-gray-200 hover:border-violet-300'
                  }`}
                >
                  <type.icon className="w-6 h-6" />
                  <span className="text-sm font-medium text-center">{type.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Logo Upload (for paid tiers) */}
          {form.tier !== 'free' && (
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <Image className="w-5 h-5 text-violet-600" />Business Logo
              </h3>
              <div className="flex items-start gap-6">
                {/* Upload Area */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-violet-500 transition-colors overflow-hidden"
                >
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="w-full h-full object-cover"
                      style={{ objectPosition: `${logoPosition.x}% ${logoPosition.y}%` }}
                    />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-gray-400 mb-1" />
                      <span className="text-xs text-gray-500">Click to upload</span>
                    </>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoSelect}
                  className="hidden"
                />

                {/* Instructions */}
                <div className="flex-1">
                  <p className="text-sm text-gray-600 mb-2">Upload your company logo</p>
                  <ul className="text-xs text-gray-500 space-y-1">
                    <li>• Recommended size: 200x200 pixels</li>
                    <li>• Formats: PNG, JPG, WebP</li>
                    <li>• Max file size: 5MB</li>
                    <li>• Square images work best</li>
                  </ul>
                  {logoPreview && (
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        onClick={() => setShowCropper(true)}
                        className="text-sm text-violet-600 hover:text-violet-700 font-medium"
                      >
                        Reposition
                      </button>
                      <button
                        type="button"
                        onClick={removeLogo}
                        className="text-sm text-red-600 hover:text-red-700 font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Business Details */}
          <div className="bg-white rounded-2xl shadow-xl p-6 space-y-4">
            <h3 className="font-bold text-lg mb-4">Business Details</h3>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block font-medium text-gray-700 mb-2">Business Name *</label>
                <input
                  type="text"
                  value={form.businessName}
                  onChange={(e) => setForm({ ...form, businessName: e.target.value })}
                  placeholder="Your business name"
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-violet-500 focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block font-medium text-gray-700 mb-2">Contact Name *</label>
                <input
                  type="text"
                  value={form.contactName}
                  onChange={(e) => setForm({ ...form, contactName: e.target.value })}
                  placeholder="Contact person"
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-violet-500 focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block font-medium text-gray-700 mb-2">Email *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="email@example.com"
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-violet-500 focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block font-medium text-gray-700 mb-2">Phone *</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="876-XXX-XXXX"
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-violet-500 focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block font-medium text-gray-700 mb-2">Parish *</label>
                <select
                  value={form.parish}
                  onChange={(e) => setForm({ ...form, parish: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-violet-500 focus:outline-none"
                >
                  {PARISHES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="block font-medium text-gray-700 mb-2">Website</label>
                <input
                  type="url"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-violet-500 focus:outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block font-medium text-gray-700 mb-2">Address</label>
              <input
                type="text"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Street address"
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-violet-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block font-medium text-gray-700 mb-2">Services Offered</label>
              <input
                type="text"
                value={form.services}
                onChange={(e) => setForm({ ...form, services: e.target.value })}
                placeholder="e.g., Traffic law, DUI defense, Motor insurance (comma separated)"
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-violet-500 focus:outline-none"
              />
            </div>

            {/* Business Hours */}
            <div className="col-span-2">
              <label className="block font-medium text-gray-700 mb-3 flex items-center gap-2">
                <Clock className="w-5 h-5 text-violet-600" />
                Business Hours
              </label>
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                  <div key={day} className="flex items-center gap-3 flex-wrap">
                    <span className="w-24 font-medium text-gray-700 capitalize">{day}</span>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.businessHours[day]?.closed}
                        onChange={(e) => setForm({
                          ...form,
                          businessHours: {
                            ...form.businessHours,
                            [day]: { ...form.businessHours[day], closed: e.target.checked }
                          }
                        })}
                        className="w-4 h-4 text-violet-600 rounded"
                      />
                      <span className="text-sm text-gray-600">Closed</span>
                    </label>
                    {!form.businessHours[day]?.closed && (
                      <>
                        <select
                          value={form.businessHours[day]?.open || '09:00'}
                          onChange={(e) => setForm({
                            ...form,
                            businessHours: {
                              ...form.businessHours,
                              [day]: { ...form.businessHours[day], open: e.target.value }
                            }
                          })}
                          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:border-violet-500 focus:outline-none bg-white"
                        >
                          {[
                            '06:00', '06:30', '07:00', '07:30', '08:00', '08:30', '09:00', '09:30',
                            '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
                            '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
                            '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00', '21:30', '22:00'
                          ].map(time => {
                            const [h, m] = time.split(':');
                            const hour = parseInt(h);
                            const ampm = hour >= 12 ? 'PM' : 'AM';
                            const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                            return <option key={time} value={time}>{hour12}:{m} {ampm}</option>;
                          })}
                        </select>
                        <span className="text-gray-500">to</span>
                        <select
                          value={form.businessHours[day]?.close || '17:00'}
                          onChange={(e) => setForm({
                            ...form,
                            businessHours: {
                              ...form.businessHours,
                              [day]: { ...form.businessHours[day], close: e.target.value }
                            }
                          })}
                          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:border-violet-500 focus:outline-none bg-white"
                        >
                          {[
                            '06:00', '06:30', '07:00', '07:30', '08:00', '08:30', '09:00', '09:30',
                            '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
                            '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
                            '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00', '21:30', '22:00'
                          ].map(time => {
                            const [h, m] = time.split(':');
                            const hour = parseInt(h);
                            const ampm = hour >= 12 ? 'PM' : 'AM';
                            const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                            return <option key={time} value={time}>{hour12}:{m} {ampm}</option>;
                          })}
                        </select>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="col-span-2">
              <label className="block font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Tell us more about your business..."
                rows={4}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-violet-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Banner Advertising Upsell - Only for Basic/Premium tiers */}
          {(form.tier === 'basic' || form.tier === 'premium') && (
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl shadow-xl overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <Megaphone className="w-7 h-7" />
                    </div>
                    <div>
                      <h3 className="font-black text-xl">Boost Your Visibility with Banner Ads</h3>
                      <p className="text-amber-100 text-sm">Exclusive offer for listed service providers</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowBannerDetails(!showBannerDetails)}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <ChevronDown className={`w-6 h-6 transition-transform ${showBannerDetails ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              </div>

              {/* Value Proposition */}
              <div className="p-5">
                <div className="grid md:grid-cols-4 gap-4 mb-6">
                  <div className="flex items-center gap-3 p-3 bg-white rounded-xl">
                    <Eye className="w-8 h-8 text-amber-500" />
                    <div>
                      <p className="font-bold text-gray-900">10,000+</p>
                      <p className="text-xs text-gray-500">Monthly Views</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white rounded-xl">
                    <Target className="w-8 h-8 text-amber-500" />
                    <div>
                      <p className="font-bold text-gray-900">Targeted</p>
                      <p className="text-xs text-gray-500">Motorist Audience</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white rounded-xl">
                    <TrendingUp className="w-8 h-8 text-amber-500" />
                    <div>
                      <p className="font-bold text-gray-900">Analytics</p>
                      <p className="text-xs text-gray-500">Track Performance</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white rounded-xl">
                    <Percent className="w-8 h-8 text-amber-500" />
                    <div>
                      <p className="font-bold text-gray-900">Up to 20%</p>
                      <p className="text-xs text-gray-500">Long-term Discount</p>
                    </div>
                  </div>
                </div>

                {/* Interest Toggle */}
                <div className="flex items-center justify-between p-4 bg-white rounded-xl border-2 border-amber-200 mb-4">
                  <div>
                    <p className="font-bold text-gray-900">I'm interested in Banner Advertising</p>
                    <p className="text-sm text-gray-500">Select your package below and our team will contact you</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const newValue = !form.interestedInBannerAd;
                      setForm({ ...form, interestedInBannerAd: newValue });
                      if (newValue) setShowBannerDetails(true);
                    }}
                    className={`relative w-16 h-8 rounded-full transition-colors ${form.interestedInBannerAd ? 'bg-amber-500' : 'bg-gray-300'}`}
                  >
                    <span className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow-md transition-transform ${form.interestedInBannerAd ? 'left-9' : 'left-1'}`} />
                  </button>
                </div>

                {/* Package Selection - Shown only when interested */}
                {form.interestedInBannerAd && (
                  <div className="space-y-5">
                    {/* Placement Selection */}
                    <div>
                      <label className="block font-bold text-gray-700 mb-3">Select Ad Placement</label>
                      <div className="grid md:grid-cols-3 gap-4">
                        {/* Top Banner */}
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, bannerAdPlacement: 'top', interestedInBannerAd: true })}
                          className={`p-4 rounded-xl border-2 text-left transition-all ${
                            form.bannerAdPlacement === 'top'
                              ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-500'
                              : 'border-gray-200 hover:border-amber-300 bg-white'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-bold text-amber-600 bg-amber-100 px-2 py-1 rounded">PREMIUM</span>
                            <Monitor className="w-5 h-5 text-gray-400" />
                          </div>
                          <h4 className="font-bold text-lg">Top Banner</h4>
                          <p className="text-sm text-gray-500 mb-2">Prime position at top of every page</p>
                          <p className="text-lg font-black text-amber-600">From J${getMonthlyPrice('top').toLocaleString()}<span className="text-sm font-normal text-gray-500">/mo</span></p>
                          <p className="text-xs text-gray-400 mt-1">728×90 desktop / 320×50 mobile</p>
                        </button>

                        {/* Bottom Banner */}
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, bannerAdPlacement: 'bottom', interestedInBannerAd: true })}
                          className={`p-4 rounded-xl border-2 text-left transition-all ${
                            form.bannerAdPlacement === 'bottom'
                              ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-500'
                              : 'border-gray-200 hover:border-amber-300 bg-white'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-bold text-blue-600 bg-blue-100 px-2 py-1 rounded">POPULAR</span>
                            <Monitor className="w-5 h-5 text-gray-400" />
                          </div>
                          <h4 className="font-bold text-lg">Bottom Banner</h4>
                          <p className="text-sm text-gray-500 mb-2">Footer position visible on scroll</p>
                          <p className="text-lg font-black text-amber-600">From J${getMonthlyPrice('bottom').toLocaleString()}<span className="text-sm font-normal text-gray-500">/mo</span></p>
                          <p className="text-xs text-gray-400 mt-1">728×90 desktop / 320×50 mobile</p>
                        </button>

                        {/* Inline Banner */}
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, bannerAdPlacement: 'inline', interestedInBannerAd: true })}
                          className={`p-4 rounded-xl border-2 text-left transition-all ${
                            form.bannerAdPlacement === 'inline'
                              ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-500'
                              : 'border-gray-200 hover:border-amber-300 bg-white'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-bold text-green-600 bg-green-100 px-2 py-1 rounded">BEST VALUE</span>
                            <Smartphone className="w-5 h-5 text-gray-400" />
                          </div>
                          <h4 className="font-bold text-lg">In-Content</h4>
                          <p className="text-sm text-gray-500 mb-2">Within content for high engagement</p>
                          <p className="text-lg font-black text-amber-600">From J${getMonthlyPrice('inline').toLocaleString()}<span className="text-sm font-normal text-gray-500">/mo</span></p>
                          <p className="text-xs text-gray-400 mt-1">Responsive size</p>
                        </button>
                      </div>
                    </div>

                    {/* Duration Selection */}
                    {form.bannerAdPlacement && (
                      <div>
                        <label className="block font-bold text-gray-700 mb-3">Select Duration</label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {DURATION_OPTIONS.map((opt) => {
                            const pkg = bannerPackages.find(
                              p => p.placement === form.bannerAdPlacement && p.duration_months === opt.months
                            );
                            const price = pkg ? pkg.price : 0;
                            const originalPrice = opt.discount > 0 ? Math.round(price / (1 - opt.discount/100)) : price;

                            return (
                              <button
                                key={opt.months}
                                type="button"
                                onClick={() => setForm({ ...form, bannerAdDuration: opt.months })}
                                className={`relative p-4 rounded-xl border-2 text-center transition-all ${
                                  form.bannerAdDuration === opt.months
                                    ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-500'
                                    : 'border-gray-200 hover:border-amber-300 bg-white'
                                }`}
                              >
                                {opt.popular && (
                                  <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-green-500 text-white text-xs font-bold rounded-full">Best</span>
                                )}
                                <p className="font-bold text-lg">{opt.label}</p>
                                {opt.discount > 0 && (
                                  <p className="text-xs text-green-600 font-bold">Save {opt.discount}%</p>
                                )}
                                <p className="text-xl font-black text-gray-900 mt-2">J${price.toLocaleString()}</p>
                                {opt.discount > 0 && (
                                  <p className="text-xs text-gray-400 line-through">J${originalPrice.toLocaleString()}</p>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Summary & Notes */}
                    {form.bannerAdPlacement && form.bannerAdDuration > 0 && (
                      <div className="bg-white rounded-xl p-4 border-2 border-amber-200">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="font-bold text-gray-900">Selected Package</p>
                            <p className="text-sm text-gray-500 capitalize">{form.bannerAdPlacement} Banner - {form.bannerAdDuration} Months</p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-black text-amber-600">J${getSelectedBannerPrice().toLocaleString()}</p>
                            <p className="text-xs text-gray-500">Total cost</p>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes (optional)</label>
                          <textarea
                            value={form.bannerAdNotes}
                            onChange={(e) => setForm({ ...form, bannerAdNotes: e.target.value })}
                            placeholder="Any specific requirements or questions about banner advertising..."
                            rows={2}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-amber-500 focus:outline-none text-sm"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                          <Info className="w-3 h-3" />
                          Our team will contact you after your listing is approved to finalize your banner ad purchase.
                        </p>
                      </div>
                    )}

                    {!form.bannerAdPlacement && (
                      <p className="text-sm text-gray-500 text-center py-4">
                        Select an ad placement above to see pricing options
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            {/* Summary before submit */}
            {form.tier && (
              <div className="mb-4 p-4 bg-gray-50 rounded-xl">
                <h4 className="font-bold text-gray-700 mb-3">Order Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Listing Tier:</span>
                    <span className="font-bold capitalize">{form.tier}</span>
                  </div>
                  {form.tier !== 'free' && (
                    <>
                      <div className="flex justify-between">
                        <span>Billing Period:</span>
                        <span className="font-bold capitalize">{form.billingPeriod}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Listing Fee:</span>
                        {form.billingPeriod === 'annual' ? (
                          <span className="font-bold">
                            J${Math.round((tiers.find(t => t.name === form.tier)?.price_monthly || 0) * 12 * 0.9).toLocaleString()}
                            <span className="text-emerald-600 text-xs ml-1">(10% off)</span>
                          </span>
                        ) : (
                          <span className="font-bold">J${(tiers.find(t => t.name === form.tier)?.price_monthly || 0).toLocaleString()}</span>
                        )}
                      </div>
                    </>
                  )}
                  {form.interestedInBannerAd && form.bannerAdPlacement && form.bannerAdDuration > 0 && (
                    <div className="flex justify-between">
                      <span>Banner Ad ({form.bannerAdDuration}mo):</span>
                      <span className="font-bold text-amber-600">J${getSelectedBannerPrice().toLocaleString()}</span>
                    </div>
                  )}
                </div>

                {/* Total */}
                {form.tier !== 'free' && (
                  <div className="border-t-2 border-gray-300 mt-4 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-gray-800">Total Due:</span>
                      <span className="text-2xl font-black text-violet-600">
                        J${(() => {
                          const listingFee = form.billingPeriod === 'annual'
                            ? Math.round((tiers.find(t => t.name === form.tier)?.price_monthly || 0) * 12 * 0.9)
                            : (tiers.find(t => t.name === form.tier)?.price_monthly || 0);
                          const bannerFee = (form.interestedInBannerAd && form.bannerAdPlacement && form.bannerAdDuration > 0)
                            ? getSelectedBannerPrice()
                            : 0;
                          return (listingFee + bannerFee).toLocaleString();
                        })()}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 text-right">
                      {form.billingPeriod === 'annual' ? 'Billed annually' : 'Billed monthly'}
                      {form.interestedInBannerAd && form.bannerAdPlacement && ' + one-time banner fee'}
                    </p>
                  </div>
                )}

                {form.tier === 'free' && (
                  <div className="border-t-2 border-gray-300 mt-4 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-gray-800">Total Due:</span>
                      <span className="text-2xl font-black text-emerald-600">FREE</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* TODO: Payment section - temporarily disabled, will return to complete
            {form.tier && form.tier !== 'free' && (
              <div className="mb-6 p-4 bg-amber-50 border-2 border-amber-200 rounded-xl">
                <h4 className="font-bold text-amber-800 mb-3 flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Payment Information
                </h4>
                <p className="text-sm text-amber-700 mb-4">
                  Please make your payment via bank transfer or PayPal, then upload your proof of payment below.
                </p>

                <div className="bg-white rounded-lg p-4 mb-4 border border-amber-200">
                  <h5 className="font-semibold text-gray-800 mb-2">Payment Options</h5>
                  <div className="space-y-2 text-sm">
                    <div>
                      <p className="font-medium text-gray-700">Bank Transfer:</p>
                      <p className="text-gray-600">Jamaica Motorist Guide • NCB Account: XXXX-XXXX-XXXX</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-700">PayPal:</p>
                      <p className="text-gray-600">info@jamaicadriversguide.com</p>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method *</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: 'bank_transfer', label: 'Bank Transfer' },
                      { value: 'paypal', label: 'PayPal' },
                      { value: 'other', label: 'Other' }
                    ].map(method => (
                      <button
                        key={method.value}
                        type="button"
                        onClick={() => setPaymentInfo({ ...paymentInfo, method: method.value })}
                        className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                          paymentInfo.method === method.value
                            ? 'border-amber-500 bg-amber-100 text-amber-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {method.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Reference (optional)</label>
                  <input
                    type="text"
                    value={paymentInfo.transactionRef}
                    onChange={(e) => setPaymentInfo({ ...paymentInfo, transactionRef: e.target.value })}
                    placeholder="e.g., Bank ref or PayPal transaction ID"
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none text-sm"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date (optional)</label>
                  <input
                    type="date"
                    value={paymentInfo.paymentDate}
                    onChange={(e) => setPaymentInfo({ ...paymentInfo, paymentDate: e.target.value })}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Proof of Payment *</label>
                  <input
                    type="file"
                    ref={paymentProofRef}
                    onChange={handlePaymentProofSelect}
                    accept="image/*,.pdf"
                    className="hidden"
                  />
                  {!paymentProofFile ? (
                    <button
                      type="button"
                      onClick={() => paymentProofRef.current?.click()}
                      className="w-full border-2 border-dashed border-amber-300 rounded-xl p-6 hover:border-amber-500 hover:bg-amber-50 transition-all text-center"
                    >
                      <Upload className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                      <p className="font-medium text-gray-700">Upload Payment Receipt</p>
                      <p className="text-xs text-gray-500 mt-1">Image or PDF (max 10MB)</p>
                    </button>
                  ) : (
                    <div className="bg-white border-2 border-amber-300 rounded-xl p-4">
                      <div className="flex items-center gap-4">
                        {paymentProofPreview ? (
                          <img
                            src={paymentProofPreview}
                            alt="Payment proof"
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-amber-100 rounded-lg flex items-center justify-center">
                            <FileText className="w-8 h-8 text-amber-600" />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-gray-800">{paymentProofFile.name}</p>
                          <p className="text-sm text-gray-500">
                            {(paymentProofFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={removePaymentProof}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <p className="text-xs text-amber-700 mt-3 flex items-center gap-1">
                  <Info className="w-3 h-3" />
                  Your listing will be reviewed and activated within 24 hours after payment verification.
                </p>
              </div>
            )}
            */}

            {/* Account Conversion Notice - Only for motorist accounts */}
            {isMotoristAccount && (
              <div className="mb-4 p-4 bg-violet-50 border-2 border-violet-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-violet-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-violet-800">Account Upgrade</h4>
                    <p className="text-sm text-violet-700 mt-1">
                      Your account is about to be converted to a <strong>business account</strong>. This means you can list a business, run banner ads on our site, and still access all the features a motorist account can.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-violet-600 hover:bg-violet-700 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {submitting ? (
                <>Processing...</>
              ) : (
                <><Send className="w-5 h-5" />Submit for Review</>
              )}
            </button>
          </div>
        </form>
      )}

      {/* Guidelines */}
      <div className="bg-gray-50 border-2 border-gray-200 rounded-2xl p-6">
        <h4 className="font-bold text-gray-900 flex items-center gap-2">
          <Info className="w-5 h-5" />Listing Guidelines
        </h4>
        <ul className="mt-3 space-y-2 text-gray-700 text-sm">
          <li>• All submissions are reviewed before being added to the directory</li>
          <li>• Businesses must be legally registered in Jamaica</li>
          <li>• Services must be related to motoring (legal, insurance, vehicle services)</li>
          <li>• We may contact you for verification</li>
          <li>• Premium listings include analytics dashboard access</li>
          <li>• Paid listings require payment proof at submission</li>
          <li>• Your listing will be activated after payment verification</li>
        </ul>
      </div>
    </div>
  );
};

export default GetListedPageEnhanced;
